import { useState, useCallback, useLayoutEffect } from 'react';
import { PostFormData, PostResponseViewModel } from 'types/post';
import { Form } from 'components/@shared/Form/Form';
import { validationSchema } from './validation';
import { FORM_ID, initialFormData } from './constants';
import { CommentFormContent } from './CommentFormContent';
import { BackdropLoader } from 'components/@shared/Loaders';
import { FormikHelpers } from 'formik';

interface Props {
    onSubmit: (values: PostFormData) => Promise<PostResponseViewModel>;
}

export const NewCommentForm = ({ onSubmit }: Props) => {
    const [anchor, setAnchor] = useState<HTMLElement | null>(null);
    const [isLoading, setIsLoading] = useState(false);

    const submitHandler = useCallback(
        (values: PostFormData, { resetForm }: FormikHelpers<PostFormData>) => {
            setIsLoading(true);
            onSubmit(values)
                .then(() => {
                    setAnchor(null);
                    setIsLoading(false);
                    resetForm();
                })
                .finally(() => setIsLoading(false));
        },
        [onSubmit],
    );

    const handleDeactivate = useCallback(() => {
        setAnchor(null);
    }, [onSubmit]);

    const handleActivate = useCallback(
        ({ currentTarget }: React.MouseEvent<HTMLDivElement>) => {
            setAnchor(currentTarget);
        },
        [onSubmit],
    );

    useLayoutEffect(() => {
        if (anchor) {
            anchor.querySelector('textarea')?.focus();
        }
    }, [anchor]);

    return (
        <Form id={FORM_ID} values={initialFormData} validationSchema={validationSchema} onSubmit={submitHandler}>
            {isLoading && <BackdropLoader />}
            <CommentFormContent isActive={!!anchor} onDeactivate={handleDeactivate} onActivate={handleActivate} />
        </Form>
    );
};
